import { Upload, X, Plus, Trash2 } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ProjectGalleryTabForm = ({
  galleryData,
  setGalleryData,
  onSave,
  setActiveTab,
  isProject,
}) => {
  const cardCls =
    "bg-white rounded-xl shadow p-8 mb-10";
  const labelCls =
    "block text-sm font-medium text-gray-700 mb-1";
  const inputCls =
    "w-full border rounded-lg px-3 py-2 shadow-sm focus:ring-2 focus:ring-blue-400 outline-none";

  const skipAutosave = useRef(false);
  const saveTimer = useRef(null);
  const isInitialLoad = useRef(true);
  const [draftId, setDraftId] = useState(null);

  useEffect(() => {
    const loadDraft = async () => {
      const storedDraftId = localStorage.getItem("draftId");
      if (!storedDraftId) {
        isInitialLoad.current = false;
        return;
      }

      const token = localStorage.getItem("token");

      try {
        const res = await axios.get(
          `${API_BASE_URL}/admin/get-draft-project/${storedDraftId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (res.data?.draft) {
          setGalleryData(res.data.draft.draft_data);
          setDraftId(res.data.draft.draft_id);
        }
      } catch (err) {
        console.error("Error loading draft:", err);
      } finally {
        isInitialLoad.current = false;
      }
    };

    if (!isProject) loadDraft();
    else isInitialLoad.current = false;

  }, [isProject, API_BASE_URL, setGalleryData]);

  const autosaveDraft = async () => {
    if (!galleryData.project_name?.trim()) return;

    const token = localStorage.getItem("token");

    const payload = {
      draft_id: draftId,
      formData: galleryData,
    };

    const res = await axios.post(
      `${API_BASE_URL}/admin/save-draft-project`,
      payload,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    if (!draftId && res.data.draft_id) {
      setDraftId(res.data.draft_id);
      localStorage.setItem("draftId", res.data.draft_id);
    }
  };

  useEffect(() => {
    if (!galleryData?.project_name?.trim()) return;

    if (skipAutosave.current) {
      skipAutosave.current = false;
      return;
    }

    if (saveTimer.current) clearTimeout(saveTimer.current);

    saveTimer.current = setTimeout(autosaveDraft, 800);

    return () => clearTimeout(saveTimer.current);
  }, [galleryData]);

  const handleDescriptionChange = (e) => {
    const { name, value } = e.target;
    setGalleryData((prev) => ({ ...prev, [name]: value }));
  };

  const addImage = () => {
    setGalleryData((prev) => ({
      ...prev,
      gallery_images: [
        ...prev.gallery_images,
        { image: "", date: "", description: "" },
      ],
    }));
  };

  const removeImage = (index) => {
    setGalleryData((prev) => ({
      ...prev,
      gallery_images: prev.gallery_images.filter((_, i) => i !== index),
    }));
  };

  const handleImageUpload = (index, e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("media", file);

    axios
      .post(`${API_BASE_URL}/admin/upload-media`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((res) => {
        const uploadedUrl = res.data.data.files?.[0]?.url;
        if (!uploadedUrl) return;

        setGalleryData((prev) => {
          const updated = [...prev.gallery_images];
          updated[index].image = uploadedUrl;
          return { ...prev, gallery_images: updated };
        });
      })
      .catch((err) => console.error("upload-media error", err));
  };

  const handleImageFieldChange = (index, field, value) => {
    setGalleryData((prev) => {
      const updated = [...prev.gallery_images];
      updated[index][field] = value;
      return { ...prev, gallery_images: updated };
    });
  };

  const handleSave = () => {
    const formattedGallery = {
      project_id: galleryData.project_id,
      gallery_description: galleryData.gallery_description,
      gallery_images: JSON.stringify(galleryData.gallery_images),
    };

    onSave(formattedGallery);
  };

  const clearForm = () => {
    skipAutosave.current = true;

    setGalleryData({
      ...galleryData,
      gallery_description: "",
      gallery_images: [],
    });

    localStorage.setItem("draftId", null);
  };

  return (
    <section className={cardCls}>

      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-blue-700">Project Gallery</h2>

        <div className="flex items-center gap-3">
          <button
            onClick={clearForm}
            className="flex items-center gap-2 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg"
          >
            <X size={18} /> Clear
          </button>

          <button
            onClick={() => setActiveTab("plan")}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
          >
            Next
          </button>
        </div>
      </div>

      <div className="mb-8">
        <label className={labelCls}>Gallery Description</label>
        <textarea
          name="gallery_description"
          value={galleryData.gallery_description}
          onChange={handleDescriptionChange}
          className={inputCls}
          placeholder="Short description about this gallery"
          rows={3}
        />
      </div>

      <div className="border-t pt-6">
        <div className="flex justify-between items-center mb-4">
          <label className={labelCls}>Gallery Images</label>

          <button
            onClick={addImage}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg"
          >
            <Plus size={18} /> Add Image
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-3 text-sm font-semibold text-gray-700 border-b">Image</th>
                <th className="p-3 text-sm font-semibold text-gray-700 border-b">Date</th>
                <th className="p-3 text-sm font-semibold text-gray-700 border-b">Description</th>
                <th className="p-3 text-center text-sm font-semibold text-gray-700 border-b">Actions</th>
              </tr>
            </thead>

          <tbody>
  {galleryData.gallery_images?.length === 0 && (
    <tr>
      <td colSpan="4" className="p-6 text-center text-gray-500">
        No images added yet.
      </td>
    </tr>
  )}

  {galleryData.gallery_images?.map((item, index) => (
    <tr
      key={index}
      className="border-b hover:bg-gray-50 align-top"
    >

      {/* IMAGE CELL */}
      <td className="p-4">
        {item.image ? (
          <div className="relative w-32 h-32 border rounded-lg overflow-hidden shadow-sm">
            <img
              src={item.image}
              alt={`Gallery ${index + 1}`}
              className="object-cover w-full h-full"
            />

            <label className="absolute bottom-2 left-2 bg-white/90 text-xs px-2 py-1 border rounded cursor-pointer hover:bg-white">
              Change
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageUpload(index, e)}
                className="hidden"
              />
            </label>
          </div>
        ) : (
          <div className="border-2 border-dashed border-gray-300 rounded-lg w-32 h-32 flex flex-col justify-center items-center gap-2 text-center p-3 overflow-hidden">

            <input
              type="file"
              id={`gallery-image-${index}`}
              accept="image/*"
              onChange={(e) => handleImageUpload(index, e)}
              className="hidden"
            />

            <label
              htmlFor={`gallery-image-${index}`}
              className="cursor-pointer flex flex-col items-center gap-2 text-gray-600"
            >
              <Upload size={22} className="text-gray-400" />

              <span className="text-xs font-medium">Upload Image</span>

              <span className="text-[10px] text-gray-500 leading-tight block">
                Size: <strong>1600×1200 px (4:3)</strong>, JPG/PNG, under 600 KB
              </span>
            </label>
          </div>
        )}
      </td>

      {/* DATE CELL */}
      <td className="p-4 align-top">
        <input
          type="date"
          value={item.date || ""}
          onChange={(e) => handleImageFieldChange(index, "date", e.target.value)}
          className={inputCls}
        />
      </td>

      {/* DESCRIPTION CELL */}
      <td className="p-4 align-top">
        <input
          type="text"
          value={item.description || ""}
          onChange={(e) =>
            handleImageFieldChange(index, "description", e.target.value)
          }
          className={inputCls}
          placeholder="Enter image description"
        />
      </td>

      {/* REMOVE ICON CELL */}
      <td className="p-4 text-center align-top">
        <button
          onClick={() => removeImage(index)}
          className="text-red-600 hover:text-red-700 flex items-center justify-center mx-auto"
        >
          <Trash2 size={20} />
           </button>
           </td>

         </tr>
         ))}
        </tbody>


          </table>
        </div>
      </div>

    </section>
  );
};

export default ProjectGalleryTabForm;
